import { Form, Input, Button, notification, Checkbox } from 'antd';
import './ResellerLoginForm.scss';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { LoginForm } from '../../components/Form/LoginForm/LoginForm';
import PublicLayout from '@/components/Layout/PublicLayout';
import { APP_LANDING_PAGE, AppRoutes, BOOK_CONSULTATION_LANDING_PAGE } from 'src/enums/ERoutes';
import { useMemo, useState } from 'react';
import { setResellerAccountInfo } from '@/store/reseller/slice';
import { resellerLogin } from '@/api/reseller';
import { useMutation } from '@tanstack/react-query';
import LocalStorageUtility from '@/utils/localStorage';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { MenuItem } from '@/interfaces/IResellerLoginForm';
import ResellerImage from './ResellerImage';
import { CheckCircle } from 'lucide-react';
import ReCaptchaVerification from '@/components/ReCaptchaVerification';
import { Reseller } from '@/types/TReseller';
import { USER_TYPES } from 'src/enums/EUser';

const ResellerLoginForm = () => {
	const [form] = Form.useForm();
	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [isVerified, setIsVerified] = useState(false);
	const [shouldVerify, setShouldVerify] = useState(false);

	const { debrand, resellerCode } = (location.state as {
		from: string;
		debrand: boolean;
		resellerCode: string;
	}) || {
		from: '/',
		debrand: false,
	};

	const mutation = useMutation({
		mutationKey: ['resellerLogin'],
		mutationFn: resellerLogin,
		onSuccess(data: any) {
			dispatch(
				setResellerAccountInfo({
					data: data.data as Reseller,
					authToken: data.token,
					isAuthenticated: !!data.token,
					clients: [],
				}),
			);
			LocalStorageUtility.setLocalData(LocalStorageKeys.RESELLER_AUTH_TOKEN, data.token);

			if (!data.data.isOnboardingComplete) {
				navigate(AppRoutes.RESELLER_ONBOARDING);
			} else {
				navigate(AppRoutes.RESELLER_PORTAL);
			}
		},
		onError(data) {
			notification.error({
				message: data.message,
			});
		},
	});

	const handleSubmit = async (values: { username: string; password: string }) => {
		if (!isVerified) {
			setShouldVerify(true);
			return;
		}
		mutation.mutate({ ...values, from: USER_TYPES.RESELLER });
	};

	const items: MenuItem[] = [
		{ key: 1, target: '_blank', url: APP_LANDING_PAGE, label: 'Product' },
		{ key: 2, target: '_self', url: AppRoutes.RESELLER_PORTAL, label: 'Login' },
		// { key: 3, target: '_self', url: AppRoutes.REGISTER_RESELLER, label: 'Register' },
		{ key: 4, target: '_blank', url: BOOK_CONSULTATION_LANDING_PAGE, label: 'Book Consultation' },
		{ key: 5, target: '_blank', url: APP_LANDING_PAGE, label: 'Contact' },
	];

	const filteredItems = useMemo(
		() =>
			items.filter((item) => {
				if (location.pathname === AppRoutes.RESELLER_PORTAL && item.label === 'Login') {
					return false;
				}
				if (location.pathname === AppRoutes.REGISTER_RESELLER && item.label === 'Register') {
					return false;
				}
				return true;
			}),
		[location.pathname],
	);

	const FormItems = () => (
		<>
			{debrand && <ResellerImage debrand={debrand} resellerCode={resellerCode} />}

			<div className={`${baseClass}_info-container`}>
				<h2 className={`${baseClass}_info-container_heading`}>Welcome Back</h2>
				<span className={`${baseClass}_info-container_title`}> Reseller Admin Portal</span>
			</div>
			<Form.Item
				name="username"
				label="Email"
				className={`${baseClass}__input-item`}
				rules={[
					{ required: true, message: "Email can't be blank!" },
					{ type: 'email', message: 'Please enter a valid email address!' },
				]}
			>
				<Input placeholder="Email" />
			</Form.Item>

			<Form.Item
				name="password"
				rules={[{ required: true, message: "Password can't be blank!" }]}
				label="Password"
				className={`${baseClass}__password-input-item`}
			>
				<Input.Password placeholder="Password" />
			</Form.Item>

			<Form.Item>
				<div className="max-w-[342px]">
					{!shouldVerify && !isVerified ? (
						<div className="bg-gray-50 p-3 rounded-md border hover:bg-gray-100 transition-colors">
							<Checkbox
								onChange={(e) => {
									if (e.target.checked) {
										setShouldVerify(true);
									}
								}}
								className="flex items-center"
							>
								<span className="text-gray-600 text-sm font-semibold">Verify you are human</span>
							</Checkbox>
						</div>
					) : shouldVerify ? (
						<div className="text-gray-500 flex items-center justify-center gap-2 p-4">
							<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500" />
							<span>Verifying you're human...</span>
						</div>
					) : isVerified ? (
						<div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-lg">
							<CheckCircle className="h-5 w-5" />
							<span className="font-medium">Human verification completed.</span>
						</div>
					) : null}
				</div>
				<ReCaptchaVerification
					trigger={shouldVerify}
					onVerificationComplete={(success) => {
						setIsVerified(success);
						setShouldVerify(false);
					}}
					className="hidden"
				/>
			</Form.Item>

			<Form.Item>
				<Button
					loading={mutation.isPending}
					type="primary"
					htmlType="submit"
					className={`${baseClass}__submit-button`}
					disabled={shouldVerify || !isVerified}
				>
					Submit
				</Button>
			</Form.Item>
			{/* <Form.Item className={`${baseClass}__footer`}>
        <span className="register-now">
          Don't have an account? <Link to="/register_reseller">Register Now</Link>
        </span>
      </Form.Item> */}
			<Form.Item className={`${baseClass}__footer`}>
				<span className="forgot-password block text-center">
					<Link to="/reseller_portal/forgot-password">Forgot Password?</Link>
				</span>
			</Form.Item>
		</>
	);

	const baseClass = 'reseller-login';
	return (
		<PublicLayout debrand={debrand} items={filteredItems}>
			<LoginForm
				customClassName="!my-auto"
				form={form}
				formItems={<FormItems />}
				handleSubmit={handleSubmit}
				autoComplete={debrand ? 'off' : 'on'}
			/>
		</PublicLayout>
	);
};

export default ResellerLoginForm;
